// Stepper: the step and task indicators, the vertical Stepper List, and the Stepper Nav
// (a step bar with panels). Steps are written as <i80-stepper-step> children, which the
// list and the nav turn into the <li> markup Helios expects, so the list stays valid HTML.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

const STATUSES = ["incomplete", "progress", "processing", "complete"];
const STATUS_TEXT = { incomplete: "", progress: "(current)", processing: "(in progress)", complete: "(complete)" };
const TASK_ICONS = { incomplete: "circle", progress: "circle-half", processing: "loading", complete: "check-circle" };
const NAV_STATUS_TEXT = { incomplete: "", active: "(current)", complete: "(complete)" };
const NAV_INDICATOR_STATUS = { incomplete: "incomplete", active: "progress", complete: "complete" };
const TITLE_TAGS = ["div", "h1", "h2", "h3", "h4", "h5", "h6"];
const SVG = "http://www.w3.org/2000/svg";
const HEXAGON = "m3.664 6.264 6.99-4.42a2.5 2.5 0 0 1 2.67-.002l7.01 4.422A2.5 2.5 0 0 1 21.5 8.38v7.242a2.5 2.5 0 0 1-1.166 2.115l-7.01 4.422a2.5 2.5 0 0 1-2.67-.002l-6.99-4.42A2.5 2.5 0 0 1 2.5 15.623V8.377a2.5 2.5 0 0 1 1.164-2.113Z";

/** The hexagon outline the step indicator draws behind the number. */
function hexagon() {
  const svg = document.createElementNS(SVG, "svg");
  for (const [name, value] of Object.entries({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: SVG, "aria-hidden": "true" })) {
    svg.setAttribute(name, value);
  }
  const path = document.createElementNS(SVG, "path");
  path.setAttribute("d", HEXAGON);
  path.setAttribute("stroke-width", "1");
  svg.append(path);
  return svg;
}

/** The hexagon with a number, a check or a spinner: I80::Stepper::Step::Indicator. */
function stepIndicator(status, text, { interactive = false, className = "", hidden = false } = {}) {
  const inside = status === "processing" || status === "complete"
    ? icon(status === "processing" ? "loading" : "check", { className: "i80-stepper-indicator-step__icon" })
    : h("span", { class: "i80-text i80-typography-body-100 i80-font-weight-medium i80-stepper-indicator-step__text" }, text);
  return h("div", {
    class: ["i80-stepper-indicator-step", `i80-stepper-indicator-step--status-${status}`,
      interactive && "i80-stepper-indicator-step--is-interactive", className],
    "aria-hidden": hidden ? "true" : null,
  },
  h("div", { class: "i80-stepper-indicator-step__svg-hexagon" }, hexagon()),
  h("div", { class: "i80-stepper-indicator-step__status" }, inside));
}

/** Just the status icon: I80::Stepper::Task::Indicator. */
function taskIndicator(status, { interactive = false } = {}) {
  return h("div", {
    class: ["i80-stepper-indicator-task", `i80-stepper-indicator-task--status-${status}`,
      interactive && "i80-stepper-indicator-task--is-interactive"],
  }, icon(TASK_ICONS[status], { className: "i80-stepper-indicator-task__icon" }));
}

/** The newlines left behind where the step tags were: they are not content. */
function dropBlankText(host) {
  for (const node of [...host.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) node.remove();
  }
}

/** An attribute of a plain marker child (<i80-stepper-step> is not an element of its own). */
function pick(el, name, allowed, fallback) {
  const value = el.getAttribute(name);
  return allowed.includes(value) ? value : fallback;
}

/**
 * What the author wrote in a step: the title (the title attribute, a slot="title" child,
 * or the plain text in the tag), the description, and whatever is left as the content.
 */
function stepParts(el) {
  const slots = { title: [], description: [], content: [] };
  for (const node of [...el.childNodes]) {
    const slot = node.nodeType === Node.ELEMENT_NODE ? node.getAttribute("slot") : null;
    if (slot === "title" || slot === "description") slots[slot].push(node);
    else if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) continue;
    else slots.content.push(node);
  }
  const titleText = el.getAttribute("title");
  if (titleText !== null) {
    slots.title = [document.createTextNode(titleText)];
  } else if (!slots.title.length) {
    slots.title = slots.content.filter((n) => n.nodeType === Node.TEXT_NODE);
    slots.content = slots.content.filter((n) => n.nodeType !== Node.TEXT_NODE);
  }
  const description = el.getAttribute("description");
  if (description !== null) slots.description = [document.createTextNode(description)];
  for (const node of [...slots.title, ...slots.description]) {
    if (node.nodeType === Node.ELEMENT_NODE) node.removeAttribute("slot");
  }
  return slots;
}

/**
 * The step title: "Step 3" for screen readers, the title itself, then the status in
 * words. The single spaces are there because Ember's template renders them too.
 */
function stepTitle(tag, classes, number, nodes, statusText) {
  return h(tag, { class: classes },
    h("span", { class: "sr-only" }, `Step ${number}`), " ", ...nodes, " ",
    h("span", { class: "sr-only", "data-i80-part": "status" }, statusText));
}

// --- Indicator on its own ------------------------------------------------

/**
 * <i80-stepper-indicator status="complete" text="2"></i80-stepper-indicator>
 * <i80-stepper-indicator type="task" status="processing"></i80-stepper-indicator>
 *
 * The indicator alone, for a table row or a list of your own. The list and the nav
 * below draw their own, so you rarely need this one.
 */
export class I80StepperIndicator extends I80Element {
  static get observedAttributes() {
    return ["type", "status", "text", "interactive"];
  }

  setup() {
    this._box = this.part("indicator");
    if (!this._box) {
      this._box = h("div", { "data-i80-part": "indicator" });
      this.append(this._box);
    }
  }

  update() {
    const status = this.choice("status", STATUSES, "incomplete");
    const interactive = this.flag("interactive");
    const next = this.choice("type", ["step", "task"], "step") === "task"
      ? taskIndicator(status, { interactive })
      : stepIndicator(status, this.getAttribute("text") ?? "", { interactive });
    next.setAttribute("data-i80-part", "indicator");
    this._box.replaceWith(next);
    this._box = next;
  }
}

// --- Stepper List -------------------------------------------------------

/** One <li> of the vertical list, built from an <i80-stepper-step> the author wrote. */
function listStep(el, tag, number) {
  const status = pick(el, "status", STATUSES, "incomplete");
  const { title, description, content } = stepParts(el);
  const text = h("div", { class: "i80-stepper-list__step-text" },
    stepTitle(tag, "i80-text i80-typography-body-200 i80-font-weight-semibold i80-foreground-strong i80-stepper-list__step-title",
      number, title, STATUS_TEXT[status]));
  // The CSS hides these two with :empty, so they are only there when there is something in them.
  if (description.length) {
    text.append(h("div", { class: "i80-text i80-typography-body-100 i80-font-weight-regular i80-foreground-primary i80-stepper-list__step-description" }, description));
  }
  if (content.length) text.append(h("div", { class: "i80-stepper-list__step-content" }, content));
  return h("li", { class: `i80-stepper-list__step i80-stepper-list__step--${status}`, id: nextId("step") },
    h("div", { class: "i80-stepper-list__step-progress" },
      stepIndicator(status, String(number), { className: "i80-stepper-list__step-indicator", hidden: true })),
    text);
}

/**
 * <i80-stepper-list label="Setting up your cluster">
 *   <i80-stepper-step status="complete">Create a project</i80-stepper-step>
 *   <i80-stepper-step status="progress" title="Add a cluster" description="Pick a region">
 *     <i80-button>Add cluster</i80-button>
 *   </i80-stepper-step>
 *   <i80-stepper-step>Invite your team</i80-stepper-step>
 * </i80-stepper-list>
 *
 * A vertical list of steps with their status. Each step's number comes from its place in
 * the list; a step's text is its title, and elements inside it become its content.
 */
export class I80StepperList extends I80Element {
  static get observedAttributes() {
    return ["label", "title-tag"];
  }

  setup() {
    let root = this.part("list");
    if (!root) {
      root = h("ol", { "data-i80-part": "list" });
      this.append(root);
    }
    this._root = root;
    // A clone carries the original's ids; every step gets its own.
    for (const li of root.querySelectorAll(":scope > li")) li.id = nextId("step");
    this.adopt();
  }

  adopt() {
    const tag = this.choice("title-tag", TITLE_TAGS, "div");
    for (const step of [...this.children].filter((c) => c.localName === "i80-stepper-step")) {
      this._root.append(listStep(step, tag, this._root.children.length + 1));
      step.remove();
    }
    dropBlankText(this);
  }

  update() {
    this._root.className = "i80-stepper-list";
    const label = this.getAttribute("label");
    if (label) this._root.setAttribute("aria-label", label);
    else this._root.removeAttribute("aria-label");
    retagTitles(this._root, ".i80-stepper-list__step-title", this.choice("title-tag", TITLE_TAGS, "div"));
  }
}

/** The heading level can change after the steps are built, so the title element is rebuilt. */
function retagTitles(root, selector, tag) {
  for (const old of root.querySelectorAll(selector)) {
    if (old.localName === tag) continue;
    const next = h(tag, { class: old.className }, ...old.childNodes);
    old.replaceWith(next);
  }
}

// --- Stepper Nav --------------------------------------------------------

/** One <li> of the step bar. Interactive steps are buttons in a tablist. */
function navStep(el, tag, number, interactive) {
  const { title, description } = stepParts(el);
  const text = h("div", { class: "i80-stepper-nav__step-text" },
    stepTitle(tag, "i80-text i80-typography-body-200 i80-font-weight-semibold i80-stepper-nav__step-title", number, title, ""));
  if (description.length) {
    text.append(h("div", { class: "i80-text i80-typography-body-100 i80-font-weight-regular i80-stepper-nav__step-description" }, description));
  }
  const inner = interactive
    ? h("button", { class: "i80-stepper-nav__step-content i80-stepper-nav__step-button", type: "button", role: "tab" })
    : h("div", { class: "i80-stepper-nav__step-content" });
  inner.append(h("div", { class: "i80-stepper-nav__step-progress" }), text);
  return h("li", { class: "i80-stepper-nav__step" }, inner);
}

/**
 * <i80-stepper-nav label="Create a cluster" current="1">
 *   <i80-stepper-step title="Details"></i80-stepper-step>
 *   <i80-stepper-step title="Network"></i80-stepper-step>
 *   <i80-stepper-step title="Review"></i80-stepper-step>
 *   <i80-stepper-panel>…</i80-stepper-panel>
 *   <i80-stepper-panel>…</i80-stepper-panel>
 *   <i80-stepper-panel>…</i80-stepper-panel>
 * </i80-stepper-nav>
 *
 * The horizontal step bar of a wizard. current is the index of the step in progress;
 * the steps before it are complete, the ones after are not. Steps and panels are paired
 * by order. Unless interactive="false", a completed step is a button that goes back to
 * it (the arrow keys move between the reachable steps), and it fires i80-change {index}.
 */
export class I80StepperNav extends I80Element {
  static get observedAttributes() {
    return ["label", "current", "interactive", "title-tag"];
  }

  setup() {
    let root = this.part("nav");
    if (!root) {
      root = h("div", { "data-i80-part": "nav" },
        h("div", { class: "i80-stepper-nav__progress-bar" }),
        h("ol", { class: "i80-stepper-nav__list", "data-i80-part": "steps" }));
      this.append(root);
    }
    this._root = root;
    this._list = root.querySelector('[data-i80-part="steps"]');
    // On a clone the markup is already there: read back whether it was built interactive,
    // and drop the ids it copied so this instance gets its own.
    const built = root.querySelector(".i80-stepper-nav__step-content");
    if (built) this._interactive = built.localName === "button";
    for (const el of root.querySelectorAll(".i80-stepper-nav__step-content, .i80-stepper-nav__panel")) el.removeAttribute("id");
    this.adopt();
  }

  /** interactive is read once, when the first steps are built: it decides button or div. */
  get interactive() {
    if (this._interactive === undefined) this._interactive = (this.getAttribute("interactive") ?? "true") !== "false";
    return this._interactive;
  }

  get current() {
    const steps = this._list.children.length;
    return Math.min(Math.max(parseInt(this.getAttribute("current") ?? "0", 10) || 0, 0), steps);
  }

  set current(index) {
    this.setAttribute("current", String(index));
  }

  adopt() {
    const tag = this.choice("title-tag", TITLE_TAGS, "div");
    for (const child of [...this.children]) {
      if (child.localName === "i80-stepper-step") {
        const li = navStep(child, tag, this._list.children.length + 1, this.interactive);
        this._list.append(li);
        child.remove();
      } else if (child.localName === "i80-stepper-panel") {
        this._root.append(h("section", {
          class: "i80-stepper-nav__panel",
          role: this.interactive ? "tabpanel" : null,
        }, ...child.childNodes));
        child.remove();
      }
    }
    dropBlankText(this);
    this._wire();
  }

  /** Ids pair each step with its panel; the listeners live on the step buttons. */
  _wire() {
    const steps = [...this._list.children];
    const panels = [...this._root.querySelectorAll(":scope > .i80-stepper-nav__panel")];
    // A WeakSet, not a data- attribute: a clone would carry the attribute but no listeners.
    this._wired ??= new WeakSet();
    steps.forEach((li, i) => {
      const content = li.querySelector(".i80-stepper-nav__step-content");
      if (!content.id) content.id = nextId("step");
      if (content.localName === "button" && !this._wired.has(content)) {
        this._wired.add(content);
        content.addEventListener("click", () => this.select([...this._list.children].indexOf(li)));
        content.addEventListener("keydown", (e) => this._key(e, li));
      }
      const panel = panels[i];
      if (!panel) return;
      if (!panel.id) panel.id = nextId("panel");
      if (content.localName === "button") content.setAttribute("aria-controls", panel.id);
      panel.setAttribute("aria-labelledby", content.id);
    });
  }

  update() {
    const interactive = this.interactive;
    this._root.className = interactive ? "i80-stepper-nav i80-stepper-nav--interactive" : "i80-stepper-nav";
    const label = this.getAttribute("label");
    if (label) this._list.setAttribute("aria-label", label);
    else this._list.removeAttribute("aria-label");
    if (interactive) this._list.setAttribute("role", "tablist");
    else this._list.removeAttribute("role");
    retagTitles(this._list, ".i80-stepper-nav__step-title", this.choice("title-tag", TITLE_TAGS, "div"));

    const steps = [...this._list.children];
    const current = this.current;
    steps.forEach((li, i) => {
      const status = i === current ? "active" : i < current ? "complete" : "incomplete";
      li.className = `i80-stepper-nav__step i80-stepper-nav__step--${status}`;
      li.querySelector(".i80-stepper-nav__step-progress").replaceChildren(
        stepIndicator(NAV_INDICATOR_STATUS[status], String(i + 1), { interactive, className: "i80-stepper-nav__step-indicator", hidden: true }));
      li.querySelector('[data-i80-part="status"]').textContent = NAV_STATUS_TEXT[status];
      const content = li.querySelector(".i80-stepper-nav__step-content");
      if (content.localName === "button") {
        li.setAttribute("role", "presentation");
        li.removeAttribute("aria-current");
        content.setAttribute("aria-selected", String(status === "active"));
        content.setAttribute("aria-disabled", String(status === "incomplete"));
        if (status === "active") content.removeAttribute("tabindex");
        else content.setAttribute("tabindex", "-1");
      } else {
        li.removeAttribute("role");
        li.setAttribute("aria-current", status === "active" ? "step" : "false");
      }
    });
    for (const [i, panel] of [...this._root.querySelectorAll(":scope > .i80-stepper-nav__panel")].entries()) {
      panel.hidden = i !== current;
    }
    this._progressBar(steps.length, current);
  }

  /** The filled part of the bar stops in the middle of the step in progress. */
  _progressBar(steps, current) {
    let width = 0;
    let offset = 0;
    if (steps) {
      if (current >= steps) width = 100;
      else {
        width = (current / steps + 1 / steps / 2) * 100;
        offset = 16;
      }
    }
    this._root.style.setProperty("--i80-stepper-nav-progress-bar-width", `calc(${width}% - ${offset}px)`);
  }

  /** Go back to a completed step. The step in progress and the ones after it do nothing. */
  select(index) {
    if (index < 0 || index >= this.current) return;
    this.current = index;
    this.dispatchEvent(new CustomEvent("i80-change", { bubbles: true, detail: { index } }));
  }

  _key(event, li) {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const steps = [...this._list.children];
    // Only the steps up to and including the one in progress can be reached.
    const last = Math.min(this.current, steps.length - 1);
    const to = (steps.indexOf(li) + step + last + 1) % (last + 1);
    steps[to]?.querySelector(".i80-stepper-nav__step-content")?.focus();
  }
}

define("i80-stepper-indicator", I80StepperIndicator);
define("i80-stepper-list", I80StepperList);
define("i80-stepper-nav", I80StepperNav);
